// React
import React from 'react';
import {useState, useCallback, useEffect, useRef} from 'react';

// MUI
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';

// others
import { useAuth } from 'react-oidc-context';

import * as fbproto from './messages_pb.js';
import './App.css';
import { useFetch } from './hooks/useFetch.jsx';
import PhConfig from './configs/PhConfig.jsx';
import RunConfig from './configs/RunConfig.jsx';
import RenameDialog, {RenameIconButton, hoverRenameSx} from './RenameDialog.jsx';
import AdvancedSection, {techDetailsSx} from './properties/AdvancedSection.jsx';

const DEVICE_TYPE_LABELS = {
    [fbproto.DeviceType.DT_PH]: 'pH sensor',
    [fbproto.DeviceType.DT_EC]: 'EC sensor',
    [fbproto.DeviceType.DT_TEMPERATURE]: 'Temperature sensor',
    [fbproto.DeviceType.DT_HUMIDITY]: 'Humidity sensor',
    [fbproto.DeviceType.DT_CO2]: 'CO2 sensor',
    [fbproto.DeviceType.DT_PUMP]: 'Pump',
    [fbproto.DeviceType.DT_VALVE]: 'Valve',
    [fbproto.DeviceType.DT_FAN]: 'Fan',
    [fbproto.DeviceType.DT_POWER]: 'Power outlet',
    [fbproto.DeviceType.DT_MOISTURE]: 'Moisture sensor',
    [fbproto.DeviceType.DT_RELAY]: 'Relay',
};

const DEVICE_ASSEMBLY_TYPE_LABELS = {
    [fbproto.DeviceAssemblyType.DAT_PH_GENERIC]: 'pH',
    [fbproto.DeviceAssemblyType.DAT_EC_GENERIC]: 'EC',
    [fbproto.DeviceAssemblyType.DAT_TEMPERATURE_GENERIC]: 'Temperature',
    [fbproto.DeviceAssemblyType.DAT_HUMIDITY_GENERIC]: 'Humidity',
    [fbproto.DeviceAssemblyType.DAT_CO2_GENERIC]: 'CO2',
    [fbproto.DeviceAssemblyType.DAT_PUMP_GENERIC]: 'Pump',
    [fbproto.DeviceAssemblyType.DAT_VALVE_GENERIC]: 'Valve',
    [fbproto.DeviceAssemblyType.DAT_FAN_GENERIC]: 'Fan',
    [fbproto.DeviceAssemblyType.DAT_POWER_GENERIC]: 'Power',
    [fbproto.DeviceAssemblyType.DAT_MOISTURE_GENERIC]: 'Moisture',
    [fbproto.DeviceAssemblyType.DAT_RELAY_GENERIC]: 'Relay',
    [fbproto.DeviceAssemblyType.DAT_AM2301B]: 'AM2301B',
    [fbproto.DeviceAssemblyType.DAT_ACD1100]: 'ACD1100',
    [fbproto.DeviceAssemblyType.DAT_ZTS3002]: 'ZTS3002',
};

function enumName(enumObj, value) {
    for (const key of Object.keys(enumObj)) {
        if (enumObj[key] === value) {
            return key;
        }
    }
    return "" + value;
}

function typeLabel(deviceType) {
    return DEVICE_TYPE_LABELS[deviceType] || 'Device';
}

function assemblyTypeLabel(deviceAssemblyType) {
    return DEVICE_ASSEMBLY_TYPE_LABELS[deviceAssemblyType] || 'Device';
}

// actuators (pump/valve/fan/relay) can be run manually from the device page
function isActuatorDeviceType(deviceType) {
    return deviceType === fbproto.DeviceType.DT_PUMP ||
        deviceType === fbproto.DeviceType.DT_VALVE ||
        deviceType === fbproto.DeviceType.DT_FAN ||
        deviceType === fbproto.DeviceType.DT_RELAY;
}

// fallback display name for an unnamed device assembly ("pH device", ...)
function assemblyFallbackName(deviceAssemblyType) {
    const label = assemblyTypeLabel(deviceAssemblyType);
    return label === 'Device' ? 'Device' : label + ' device';
}

// the name a sub-device is known by in the UI: its own name when set,
// otherwise a muted type-based fallback
function deviceDisplayName(assembly, device) {
    const name = (device.deviceName || "").trim();
    if (name !== "" && name !== assembly.name) {
        return {text: name, isFallback: false};
    }
    return {text: typeLabel(device.deviceType), isFallback: true};
}

// a sub-device row in the left list only appears when it carries information
// the device row does not: the device has several sub-devices, or this
// sub-device has a name of its own
function visibleDevices(assembly) {
    if (assembly.devices.length > 1) {
        return assembly.devices;
    }
    return assembly.devices.filter(d => {
        const name = (d.deviceName || "").trim();
        return name !== "" && name !== assembly.name;
    });
}

// same solid triangle glyph as AdvancedSection in the flow page
function ExpandTriangle(props) {
    return (
        <Box component="span" sx={{fontSize: '0.875rem', color: 'text.secondary', display: 'inline-block', width: '1em', textAlign: 'center', transform: props.expanded ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms'}}>&#9660;</Box>
    );
}

export default function Devices(props) {
    const [assemblies, setAssemblies] = useState({});
    // assembly id -> whether its sub-device rows are expanded
    const [expanded, setExpanded] = useState({});
    // controller id -> controller name
    const [controllerNames, setControllerNames] = useState({});
    // selection: {kind:'assembly'|'device', assemblyId, deviceId?}
    // kind 'device' highlights that sub-device's card on the device page
    const [selected, setSelected] = useState(null);
    // rename dialog target: {kind, entityId, initialName}
    const [renameTarget, setRenameTarget] = useState(null);
    // sub-device id whose card should play the one-shot selection flash
    const [flashDeviceId, setFlashDeviceId] = useState(null);
    const fetchWrapper = useFetch();
    const auth = useAuth();
    const cardRefs = useRef({});

    useEffect(() => {
        // TODO: pull up one level, so devices are shared between Dashboard and Devices pages. do loading UI. either fetch() into parent state, or use react-router's loader/useLoaderData/useRouteLoaderData. how to chain calls between parent & child, use useEffect on parent state?
        const userIdObj = {userId: auth.userId}
        fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/device-assemblies/userid`, userIdObj)
            .then(response => {
                return response.json();
            })
            .then(responseObj => {
                // sample response: {"deviceAssemblies":[{"deviceAssemblyId":2,"controllerId":4,"deviceAssemblyType":2,"deviceAssemblyName":"some EC probe","port":1,"devices":[{"deviceId":5,"deviceType":1,"deviceName":"pH sensor","subport":0}]}]}
                console.log("get device assemblies body = " + JSON.stringify(responseObj));
                var myAssemblies = {};
                var myExpanded = {};
                if (responseObj.deviceAssemblies) {
                    for (const assembly of responseObj.deviceAssemblies) {
                        var newAssembly = {
                            deviceAssemblyId: assembly.deviceAssemblyId,
                            controllerId: assembly.controllerId,
                            deviceAssemblyType: assembly.deviceAssemblyType,
                            name: assembly.deviceAssemblyName,
                            port: assembly.port,
                            devices: assembly.devices
                        };
                        myAssemblies[""+assembly.deviceAssemblyId] = newAssembly;
                        myExpanded[""+assembly.deviceAssemblyId] = visibleDevices(newAssembly).length > 0;
                    }
                }
                setAssemblies(myAssemblies);
                setExpanded(myExpanded);
            })
            .catch(error => {
            });

        fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/controllers/userid`, {})
            .then(response => {
                return response.json();
            })
            .then(responseObj => {
                // sample response: {"controllers":[{"id":4,"name":"Greenhouse Controller"}]}
                var myControllerNames = {};
                if (responseObj.controllers) {
                    for (const controller of responseObj.controllers) {
                        myControllerNames[""+controller.id] = controller.name;
                    }
                }
                setControllerNames(myControllerNames);
            })
            .catch(error => {
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // bring a sub-device's card into view when its row is picked in the list
    useEffect(() => {
        if (selected && selected.kind === 'device') {
            const card = cardRefs.current[selected.deviceId];
            if (card && card.scrollIntoView) {
                card.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    function getSelectedAssembly() {
        return selected ? assemblies[""+selected.assemblyId] : null;
    }

    function controllerDisplay(controllerId) {
        const name = controllerNames[""+controllerId];
        return name ? name + " (ID " + controllerId + ")" : "ID " + controllerId;
    }

    function handleAssemblyClick(assembly) {
        setSelected({kind:'assembly', assemblyId: assembly.deviceAssemblyId});
        setExpanded(prev => ({...prev, [""+assembly.deviceAssemblyId]: true}));
    }

    function handleDeviceClick(assembly, device) {
        setSelected({kind:'device', assemblyId: assembly.deviceAssemblyId, deviceId: device.deviceId});
        setFlashDeviceId(device.deviceId);
    }

    function handleToggleExpanded(assemblyId) {
        setExpanded(prev => ({...prev, [""+assemblyId]: !prev[""+assemblyId]}));
    }

    function handleRenamed(newName) {
        if (!renameTarget) {
            return;
        }
        setAssemblies(prev => {
            var next = {...prev};
            if (renameTarget.kind === 'deviceAssembly') {
                const key = ""+renameTarget.entityId;
                if (next[key]) {
                    next[key] = {...next[key], name: newName};
                }
            }
            else if (renameTarget.kind === 'device') {
                for (const key of Object.keys(next)) {
                    const assembly = next[key];
                    const device = assembly.devices.find(d => d.deviceId === renameTarget.entityId);
                    if (device) {
                        next[key] = {...assembly, devices: assembly.devices.map(d => d.deviceId === renameTarget.entityId ? {...d, deviceName: newName} : d)};
                        break;
                    }
                }
            }
            return next;
        });
    }

    function renderDeviceRows(assembly) {
        var tempArray = [];
        for (const device of visibleDevices(assembly)) {
            const displayName = deviceDisplayName(assembly, device);
            const deviceSelected = selected && selected.kind === 'device' && selected.deviceId === device.deviceId;
            tempArray.push(
                <ListItem key={"d"+device.deviceId} disablePadding sx={{paddingLeft:2, ...hoverRenameSx}} secondaryAction={
                    <RenameIconButton title={device.deviceName} onClick={() => setRenameTarget({kind:'device', entityId: device.deviceId, initialName: device.deviceName || ""})}/>
                }>
                    <ListItemButton selected={deviceSelected} sx={{position:'relative', height:'36px', lineHeight:'36px', alignContent:'center', borderBottom:'1px solid', borderColor:'grey.300'}} onClick={() => handleDeviceClick(assembly, device)}>
                        <ListItemText
                            primary={displayName.text}
                            primaryTypographyProps={{variant:'body2', color: displayName.isFallback ? 'text.disabled' : 'text.secondary', fontStyle: displayName.isFallback ? 'italic' : 'normal'}}
                        />
                    </ListItemButton>
                </ListItem>
            );
        }
        return tempArray;
    }

    function renderDeviceList() {
        var tempArray = [];
        Object.keys(assemblies).forEach(function(key, index) {
            var assembly = assemblies[key];
            const children = visibleDevices(assembly);
            const hasChildren = children.length > 0;
            const isExpanded = !!expanded[key];
            const assemblySelected = selected && selected.assemblyId === assembly.deviceAssemblyId;
            const assemblyName = (assembly.name || "").trim();
            tempArray.push(
                <React.Fragment key={"a"+assembly.deviceAssemblyId}>
                    <ListItem disablePadding sx={hoverRenameSx} secondaryAction={
                        <Box sx={{display:'flex', alignItems:'center', gap:0.5}}>
                            <RenameIconButton title={assembly.name} onClick={() => setRenameTarget({kind:'deviceAssembly', entityId: assembly.deviceAssemblyId, initialName: assembly.name || ""})}/>
                            {hasChildren ?
                                <ListItemButton
                                    onClick={(e) => { e.stopPropagation(); handleToggleExpanded(assembly.deviceAssemblyId); }}
                                    sx={{minWidth:0, padding:'2px', '&:hover': {backgroundColor:'transparent'}}}
                                >
                                    <ExpandTriangle expanded={isExpanded}/>
                                </ListItemButton> : null}
                        </Box>
                    }>
                        <ListItemButton selected={assemblySelected} sx={{position:'relative', height:'40px', lineHeight:'40px', alignContent:'center', borderBottom:'1px solid', borderTop:'1px solid', borderColor:'grey.300'}} onClick={() => handleAssemblyClick(assembly)}>
                            <ListItemText primary={assemblyName !== "" ? assemblyName : assemblyFallbackName(assembly.deviceAssemblyType)} primaryTypographyProps={assemblyName !== "" ? undefined : {color:'text.disabled', fontStyle:'italic'}}/>
                        </ListItemButton>
                    </ListItem>
                    {hasChildren ?
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {renderDeviceRows(assembly)}
                            </List>
                        </Collapse> : null}
                </React.Fragment>
            );
        });
        return tempArray;
    }

    function renderNameHeader(name, entityKind, entityId, isFallback, initialName) {
        return (
            <Box sx={{display:'flex', alignItems:'center', gap:1, ...hoverRenameSx}}>
                <Typography variant="h6" align='left' color={isFallback ? 'text.disabled' : 'text.primary'} fontStyle={isFallback ? 'italic' : 'normal'}>{name}</Typography>
                <RenameIconButton title={name} onClick={() => setRenameTarget({kind:entityKind, entityId:entityId, initialName: initialName || ""})}/>
            </Box>
        );
    }

    function renderTechnicalDetails(rows) {
        return (
            <AdvancedSection title="Technical details" sx={techDetailsSx}>
                {rows.map(row => (
                    <Typography key={row} align='left' variant="body2" color="text.secondary">{row}</Typography>
                ))}
            </AdvancedSection>
        );
    }

    function renderSubDeviceCard(assembly, device, isFirst) {
        const displayName = deviceDisplayName(assembly, device);
        const isSelected = selected && selected.kind === 'device' && selected.deviceId === device.deviceId;
        const isFlashing = isSelected && flashDeviceId === device.deviceId;
        return (
            <Box
                key={"d"+device.deviceId}
                ref={(el) => { cardRefs.current[device.deviceId] = el; }}
                className={isFlashing ? 'fb-card-flash' : undefined}
                sx={{
                    paddingTop: isFirst ? 0 : 2,
                    paddingBottom: 1,
                    marginTop: 2,
                    borderTop: isFirst ? 'none' : '1px solid',
                    borderTopColor: 'grey.300',
                    borderLeft: '3px solid',
                    borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                    backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                    scrollMarginTop: '8px',
                }}
            >
                <Typography align='left' variant="body2" color="text.secondary">Sub-device</Typography>
                {renderNameHeader(displayName.text, 'device', device.deviceId, displayName.isFallback, device.deviceName)}
                {renderTechnicalDetails([
                    "Sub-device ID: " + device.deviceId + " (device)",
                    "Type: " + typeLabel(device.deviceType) + " (" + enumName(fbproto.DeviceType, device.deviceType) + ")",
                    "Subport: " + device.subport,
                ])}
                {device.deviceType === fbproto.DeviceType.DT_PH ?
                    <PhConfig key={device.deviceId} device={device} controllerId={assembly.controllerId}/> :
                    null}
                {isActuatorDeviceType(device.deviceType) ?
                    <RunConfig key={device.deviceId} device={device} controllerId={assembly.controllerId}/> :
                    null}
            </Box>
        );
    }

    function renderDevicePage(assembly) {
        const assemblyName = (assembly.name || "").trim();
        return (
            <Box sx={{padding:2, display:'flex', flexDirection:'column'}}>
                {renderNameHeader(assemblyName !== "" ? assemblyName : assemblyFallbackName(assembly.deviceAssemblyType), 'deviceAssembly', assembly.deviceAssemblyId, assemblyName === "", assembly.name)}
                {renderTechnicalDetails([
                    "Device ID: " + assembly.deviceAssemblyId + " (device_assembly)",
                    "Connected controller: " + controllerDisplay(assembly.controllerId),
                    "Type: " + assemblyTypeLabel(assembly.deviceAssemblyType) + " (" + enumName(fbproto.DeviceAssemblyType, assembly.deviceAssemblyType) + ")",
                    "Port: " + assembly.port,
                ])}
                {assembly.devices.length === 0 ?
                    <Typography align='left' color="text.secondary" sx={{marginTop:2}}>This device has no sub-devices.</Typography> :
                    assembly.devices.map((device, index) => renderSubDeviceCard(assembly, device, index === 0))}
            </Box>
        );
    }

    const renderSelectedDeviceConfig = useCallback(
        () => {
            const assembly = getSelectedAssembly();
            if (!assembly) {
                return (null);
            }
            return renderDevicePage(assembly);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selected, assemblies, controllerNames, flashDeviceId]
    );

    return (

        <Box className="Devices" sx={{overflow:'hidden', display:'flex', flexDirection:'row', width:'100%', height:'100%', borderTop:'1px solid', borderColor:'grey.300' }}>

            {/* left bar - device list */}
            <List sx={{overflow:'auto', width:'20%', height:'100%', borderRight:'1px solid', borderColor:'grey.300'}}>
                {renderDeviceList()}
            </List>


            {/* right pane - device page */}
            <Box sx={{overflow:'auto', flexGrow:1, height:'100%', borderRight:'1px solid', borderColor:'grey.300'}}>
                {renderSelectedDeviceConfig()}
            </Box>

            {renameTarget ?
                <RenameDialog
                    open={!!renameTarget}
                    entityKind={renameTarget.kind}
                    entityId={renameTarget.entityId}
                    initialName={renameTarget.initialName}
                    onClose={() => setRenameTarget(null)}
                    onRenamed={handleRenamed}
                /> : null}

        </Box>
    );
}
